import styled from '@emotion/styled'
import { useField } from 'formik'
import { Field, ErrorMessage } from 'formik'
import React from 'react'

import SERVICES_DATA from '../../forms/enquiry-form/services-data.json'
import { ErrorWrapper, LabelErrorContainer } from './field'
import Notices from './lead-form-notices'

const EnquiryTypeField = (props) => {
  const [, meta, helpers] = useField(props.name)

  const { value, touched } = meta
  const { setValue, setTouched } = helpers

  const { categories } = SERVICES_DATA

  return (
    <EnquiryTypeContainer>
      <LabelErrorContainer>
        <legend className="required">
          What is the nature of your enquiry?
        </legend>
        <ErrorWrapper>
          <ErrorMessage name="enquiryType.category" />
        </ErrorWrapper>
      </LabelErrorContainer>
      <div className="categoryContainer">
        {categories.map((category) => {
          const isSelected = value.category === category.key
          return (
            <Category
              isSelected={isSelected}
              touched={touched}
              key={category.key}
            >
              <CategoryButton
                title={category.title}
                description={category.shortDesc}
                handleSelect={() => {
                  setValue({ category: category.key })
                  setTouched(true)
                }}
                isSelected={isSelected}
                touched={touched}
              />

              {isSelected && (
                <CategoryServices
                  services={category.services}
                  microcopy={category.servicesMicrocopy}
                  enquiryTypeValue={value}
                />
              )}
            </Category>
          )
        })}
        <Category
          isSelected={value.category === 'other'}
          touched={touched}
          key="other"
        >
          <CategoryButton
            title="Other"
            description="Other/non-immigration enquiries"
            handleSelect={() => {
              setValue({ category: 'other' })
              setTouched(true)
            }}
            isSelected={value.category === 'other'}
            touched={touched}
          />
        </Category>
      </div>
    </EnquiryTypeContainer>
  )
}

const CategoryServices = ({
  services: servicesProp = [],
  microcopy = 'I would like guidance regarding:',
  enquiryTypeValue,
}) => {
  const services = [
    ...servicesProp,
    {
      key: 'other',
      title: 'Other/please advise me',
    },
  ]

  return (
    <ServicesContainer>
      {services.length > 1 && (
        <>
          <p>{microcopy}</p>
          <ServicesList>
            {services.map((service) => {
              return service.archived === true ? null : (
                <label key={service.key}>
                  <Field
                    type="checkbox"
                    name="enquiryType.services"
                    value={service.key}
                  />
                  {service.title}
                </label>
              )
            })}
          </ServicesList>
        </>
      )}
      <Notices
        enquiryType={enquiryTypeValue}
        includeGlobal={false}
        showPrivacyNotice={false}
      />
    </ServicesContainer>
  )
}

const CategoryButton = ({
  title,
  description,
  handleSelect,
  isSelected,
  touched,
  ...passthrough
}) => {
  return (
    <StyledButton
      isSelected={isSelected}
      disabled={isSelected}
      onClick={handleSelect}
      type="button"
      touched={touched}
      {...passthrough}
    >
      <RadioIcon isSelected={isSelected} />
      <div className="left">
        <h4 className="title">{title}</h4>
        <p className="description">{description}</p>
      </div>
    </StyledButton>
  )
}

export default EnquiryTypeField

const StyledButton = styled.button`
  display: flex;
  width: 100%;
  align-items: center;
  text-align: left;

  padding: 16px 4px 16px 12px;
  color: ${(p) => p.theme.colors.primary};

  background: 0;
  border: none;

  &:focus {
    outline: none;
  }

  .left {
    flex-grow: 1;
    margin-right: 8px;
    color: ${(p) =>
      p.touched && !p.isSelected ? p.theme.colors.darkTint + 'cc' : 'inherit'};
  }

  .title {
    margin: 0;
    font-size: 1.8rem;
    font-weight: 600;
    letter-spacing: -0.01em;
    @media (max-width: ${(p) => p.theme.breakpoints.medium - 1}px) {
      letter-spacing: -0.02em;
    }
  }

  .description {
    font-size: 1.6rem;
    margin: 4px 0 0;
    letter-spacing: -0.01em;
    @media (max-width: ${(p) => p.theme.breakpoints.medium - 1}px) {
      letter-spacing: -0.02em;
    }
  }
`

const EnquiryTypeContainer = styled.fieldset`
  border: 0;
  padding: 0;
  margin: 0 0 12px;
  width: 100%;
  overflow: visible;

  .categoryContainer {
    border-top-right-radius: ${(p) => p.theme.radius}px;
    border-bottom-right-radius: ${(p) => p.theme.radius}px;
    margin-top: 8px;

    box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.2);
  }

  legend {
    margin: 0;
  }
`

const Category = styled.div`
  border-left: 5px solid
    ${(p) =>
      p.isSelected ? p.theme.colors.secondary : p.theme.colors.lightTintDarker};
  background: ${(p) => (p.touched && !p.isSelected ? '#f8f8f8' : '#fff')};

  &:first-of-type {
    border-top-right-radius: ${(p) => p.theme.radius}px;
  }
  &:not(:first-of-type) {
    box-shadow: ${(p) =>
      p.isSelected
        ? `inset 0 1px ${p.theme.colors.lightTintDarker}`
        : `inset 0 1px ${p.theme.colors.lightTintDarker}`};
  }
  &:last-of-type {
    border-bottom-right-radius: ${(p) => p.theme.radius}px;
  }
`

const ServicesContainer = styled.div`
  padding: 0 12px 2px;

  border-top: 1px solid transparent;

  > p {
    font-size: 1.7rem;
    margin: 0 0 4px;
  }
`

const ServicesList = styled.div`
  display: grid;
  grid-gap: 10px 12px;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));

  margin-left: 28px;
  margin-bottom: 20px;

  font-size: 1.8rem;
  line-height: 1.3;

  label {
    padding: 0;
    cursor: pointer;

    display: flex;
    align-items: center;

    & > input {
      transform: scale(1.2);
      margin-right: 12px;
    }
  }
`

const RadioIcon = styled.div`
  height: 16px;
  width: 16px;
  margin-top: 3px;
  flex: 0 0 auto;
  margin-right: 10px;
  background: ${(p) => (p.isSelected ? p.theme.colors.primary : 0)};
  box-shadow: inset 0 0 0 2.5px #fff;
  border: 2px solid ${(p) => p.theme.colors.primary};
  border-radius: 999px;
  align-self: flex-start;
`
